import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { conflicts } from '../data/conflicts';
import {
  computeCountryStats,
  categoryLabel,
  categoryColor,
  formatNumber,
  formatDeaths,
  conflictDuration,
  mapFillColor,
  mapFillOpacity,
  filterConflicts,
  getConflictById,
} from '../utils';
import type { Conflict } from '../types';
import { glossarySpan } from '../tooltip';

let map: L.Map | null = null;

export function destroyMapView(): void {
  if (map) {
    map.remove();
    map = null;
  }
}

function renderConflictDetail(panel: HTMLElement, conflict: Conflict): void {
  panel.querySelector('.detail-body')!.innerHTML = `
    <div class="conflict-detail">
      <div style="margin-bottom: 12px;">
        <span class="category-pill" style="background: ${categoryColor(conflict.category)}20; color: ${categoryColor(conflict.category)}">${categoryLabel(conflict.category)}</span>
      </div>
      <h2>${conflict.name}</h2>
      <p class="cd-description">${conflict.description}</p>
      <p style="font-size: var(--font-size-sm); color: var(--text-tertiary); margin-bottom: var(--space-lg);">
        <strong>Status:</strong> ${conflict.status}
      </p>

      <div class="cd-stats">
        <div class="cd-stat"><div class="value">${conflictDuration(conflict.startYear)}</div><div class="label">Duration</div></div>
        <div class="cd-stat"><div class="value">${formatDeaths(conflict.deaths2025)}</div><div class="label">Deaths (2025)</div></div>
        <div class="cd-stat"><div class="value">${formatDeaths(conflict.deathsCumulative)}</div><div class="label">Cumulative</div></div>
        <div class="cd-stat"><div class="value">${conflict.startYear}</div><div class="label">Start Year</div></div>
      </div>

      <div class="cd-parties">
        <div class="cd-party">
          <h4>Side A</h4>
          <ul>${conflict.sideA.map(s => `<li>${s}</li>`).join('')}</ul>
        </div>
        <div class="cd-party">
          <h4>Side B</h4>
          <ul>${conflict.sideB.map(s => `<li>${s}</li>`).join('')}</ul>
        </div>
      </div>

      <div class="detail-section">
        <h3>Countries involved</h3>
        <p style="color: var(--text-secondary); font-size: var(--font-size-sm);">
          ${conflict.countryNames.join(', ')}
        </p>
      </div>

      <div class="detail-section">
        <h3>Type</h3>
        <p style="color: var(--text-secondary); font-size: var(--font-size-sm);">
          ${glossarySpan(conflict.type === 'state-based' ? 'State-based conflict' : conflict.type === 'non-state' ? 'Non-state conflict' : conflict.type === 'one-sided' ? 'One-sided violence' : 'Mixed (state-based + non-state)')}
        </p>
      </div>
    </div>
  `;
}

export async function renderMapView(container: HTMLElement, search: string): Promise<void> {
  const countryStats = computeCountryStats();

  container.innerHTML = `
    <div class="map-container" id="map-el"></div>
    <div class="map-stats">
      <div class="stat-chip"><div class="stat-value">${conflicts.length}</div><div class="stat-label">Active conflicts</div></div>
      <div class="stat-chip"><div class="stat-value">${countryStats.size}</div><div class="stat-label">Countries affected</div></div>
      <div class="stat-chip"><div class="stat-value">${conflicts.filter(c => c.category === 'major_war').length}</div><div class="stat-label">${glossarySpan('Major war', 'Major wars')}</div></div>
    </div>
    <div class="map-legend">
      <h4>Conflict Intensity</h4>
      <div class="legend-item"><div class="legend-swatch" style="background: #7f1d1d"></div> ${glossarySpan('Major war')} (10,000+ deaths/yr)</div>
      <div class="legend-item"><div class="legend-swatch" style="background: #9a3412"></div> ${glossarySpan('Minor war')} (1,000–9,999)</div>
      <div class="legend-item"><div class="legend-swatch" style="background: #78350f"></div> ${glossarySpan('Conflict')} (100–999)</div>
      <div class="legend-item"><div class="legend-swatch" style="background: #365314"></div> ${glossarySpan('Skirmish')} (<100)</div>
      <div class="legend-item"><div class="legend-swatch" style="background: #1e293b"></div> No active conflict</div>
    </div>
    <div class="detail-panel" id="detail-panel">
      <div class="detail-header">
        <h2 id="detail-title"></h2>
        <button class="detail-close" id="detail-close">×</button>
      </div>
      <div class="detail-body" id="detail-body"></div>
    </div>
  `;

  // Init Leaflet map
  map = L.map('map-el', {
    center: [20, 10],
    zoom: 2.5,
    minZoom: 2,
    maxZoom: 8,
    zoomControl: true,
    attributionControl: true,
    worldCopyJump: true,
  });

  // Dark tile layer
  L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png', {
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>',
    subdomains: 'abcd',
    maxZoom: 19,
  }).addTo(map);

  // Labels on top
  L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}{r}.png', {
    subdomains: 'abcd',
    maxZoom: 19,
    pane: 'overlayPane',
  }).addTo(map);

  // Load GeoJSON
  try {
    const resp = await fetch('https://raw.githubusercontent.com/johan/world.geo.json/master/countries.geo.json');
    const geojson = await resp.json();

    L.geoJSON(geojson, {
      style: (feature) => {
        const iso3 = feature?.properties?.['name'] ? isoFromName(feature.properties['name'] as string) : '';
        const stats = countryStats.get(iso3);
        const score = stats ? stats.intensityScore : 0;

        // If search active, dim non-matching countries
        if (search) {
          const matchingConflicts = filterConflicts(conflicts, { search });
          const matchingIso3s = new Set(matchingConflicts.flatMap(c => c.countries));
          if (!matchingIso3s.has(iso3)) {
            return {
              fillColor: '#1e293b',
              fillOpacity: 0.05,
              weight: 0.5,
              color: '#1e293b',
            };
          }
        }

        return {
          fillColor: mapFillColor(score),
          fillOpacity: mapFillOpacity(score),
          weight: 1,
          color: '#334155',
          opacity: 0.5,
        };
      },
      onEachFeature: (feature, layer) => {
        const name = feature?.properties?.['name'] as string || '';
        const iso3 = isoFromName(name);
        const stats = countryStats.get(iso3);

        if (stats) {
          const conflictList = stats.conflicts.map(id => {
            const c = getConflictById(id);
            return c ? `<br/>• ${c.name} <span class="popup-stat">(${categoryLabel(c.category)})</span>` : '';
          }).join('');

          layer.bindPopup(`
            <div class="popup-title">${stats.name}</div>
            <div><span class="popup-stat">${stats.conflictCount}</span> active conflict${stats.conflictCount > 1 ? 's' : ''}</div>
            <div>~<span class="popup-stat">${formatNumber(stats.totalDeaths2025)}</span> fatalities (2025)</div>
            ${conflictList}
          `, { maxWidth: 300 });
        } else {
          layer.bindPopup(`<div class="popup-title">${name}</div><div style="color: var(--text-tertiary)">No active conflicts</div>`);
        }

        layer.on('click', () => {
          if (stats && stats.conflicts.length > 0) {
            showCountryDetail(stats.name, stats.conflicts);
          }
        });
      },
    }).addTo(map);
  } catch {
    container.innerHTML += '<div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);color:var(--status-bad);font-size:1.2em;">Failed to load map data. Please refresh.</div>';
  }

  // Detail panel close
  const panel = document.getElementById('detail-panel')!;
  document.getElementById('detail-close')!.addEventListener('click', () => {
    panel.classList.remove('open');
  });

  function showCountryDetail(countryName: string, conflictIds: string[]) {
    const title = document.getElementById('detail-title')!;
    const body = document.getElementById('detail-body')!;
    title.textContent = countryName;

    body.innerHTML = `
      <div class="detail-section">
        <h3>Active Conflicts (${conflictIds.length})</h3>
        ${conflictIds.map(id => {
          const c = getConflictById(id);
          if (!c) return '';
          return `
            <div class="conflict-card" data-id="${c.id}">
              <div class="cc-name">
                <span class="category-pill" style="background: ${categoryColor(c.category)}20; color: ${categoryColor(c.category)}">${categoryLabel(c.category)}</span>
                ${c.name}
              </div>
              <div class="cc-meta">
                Since ${c.startYear} · ~${formatDeaths(c.deaths2025)} deaths (2025) · ${c.status}
              </div>
            </div>
          `;
        }).join('')}
      </div>
    `;

    // Click conflict cards for detail
    body.querySelectorAll('.conflict-card').forEach(card => {
      card.addEventListener('click', () => {
        const id = (card as HTMLElement).dataset.id!;
        const conflict = getConflictById(id);
        if (conflict) renderConflictDetail(panel, conflict);
      });
    });

    panel.classList.add('open');
  }
}

// ── Country name → ISO3 mapping ──
// GeoJSON uses full names; we need ISO3 codes
const nameToIso3: Record<string, string> = {
  'Afghanistan': 'AFG', 'Albania': 'ALB', 'Algeria': 'DZA', 'Angola': 'AGO',
  'Argentina': 'ARG', 'Armenia': 'ARM', 'Australia': 'AUS', 'Austria': 'AUT',
  'Azerbaijan': 'AZE', 'Bahrain': 'BHR', 'Bangladesh': 'BGD', 'Belarus': 'BLR',
  'Belgium': 'BEL', 'Belize': 'BLZ', 'Benin': 'BEN', 'Bhutan': 'BTN',
  'Bolivia': 'BOL', 'Bosnia and Herzegovina': 'BIH', 'Botswana': 'BWA',
  'Brazil': 'BRA', 'Brunei': 'BRN', 'Bulgaria': 'BGR', 'Burkina Faso': 'BFA',
  'Burundi': 'BDI', 'Cambodia': 'KHM', 'Cameroon': 'CMR', 'Canada': 'CAN',
  'Central African Republic': 'CAF', 'Chad': 'TCD', 'Chile': 'CHL',
  'China': 'CHN', 'Colombia': 'COL', 'Congo': 'COG',
  'Democratic Republic of the Congo': 'COD', 'Costa Rica': 'CRI',
  'Croatia': 'HRV', 'Cuba': 'CUB', 'Cyprus': 'CYP',
  'Czech Republic': 'CZE', 'Denmark': 'DNK', 'Djibouti': 'DJI',
  'Dominican Republic': 'DOM', 'East Timor': 'TLS', 'Ecuador': 'ECU',
  'Egypt': 'EGY', 'El Salvador': 'SLV', 'Equatorial Guinea': 'GNQ',
  'Eritrea': 'ERI', 'Estonia': 'EST', 'Ethiopia': 'ETH', 'Fiji': 'FJI',
  'Finland': 'FIN', 'France': 'FRA', 'Gabon': 'GAB', 'Gambia': 'GMB',
  'Georgia': 'GEO', 'Germany': 'DEU', 'Ghana': 'GHA', 'Greece': 'GRC',
  'Guatemala': 'GTM', 'Guinea': 'GIN', 'Guinea Bissau': 'GNB',
  'Guinea-Bissau': 'GNB', 'Guyana': 'GUY', 'Haiti': 'HTI', 'Honduras': 'HND',
  'Hungary': 'HUN', 'Iceland': 'ISL', 'India': 'IND', 'Indonesia': 'IDN',
  'Iran': 'IRN', 'Iraq': 'IRQ', 'Ireland': 'IRL', 'Israel': 'ISR',
  'Italy': 'ITA', 'Ivory Coast': 'CIV', 'Jamaica': 'JAM', 'Japan': 'JPN',
  'Jordan': 'JOR', 'Kazakhstan': 'KAZ', 'Kenya': 'KEN', 'Kosovo': 'XKX',
  'Kuwait': 'KWT', 'Kyrgyzstan': 'KGZ', 'Laos': 'LAO', 'Latvia': 'LVA',
  'Lebanon': 'LBN', 'Lesotho': 'LSO', 'Liberia': 'LBR', 'Libya': 'LBY',
  'Lithuania': 'LTU', 'Luxembourg': 'LUX', 'Macedonia': 'MKD',
  'Madagascar': 'MDG', 'Malawi': 'MWI', 'Malaysia': 'MYS', 'Mali': 'MLI',
  'Mauritania': 'MRT', 'Mexico': 'MEX', 'Moldova': 'MDA', 'Mongolia': 'MNG',
  'Montenegro': 'MNE', 'Morocco': 'MAR', 'Mozambique': 'MOZ',
  'Myanmar': 'MMR', 'Namibia': 'NAM', 'Nepal': 'NPL', 'Netherlands': 'NLD',
  'New Zealand': 'NZL', 'Nicaragua': 'NIC', 'Niger': 'NER', 'Nigeria': 'NGA',
  'North Korea': 'PRK', 'Norway': 'NOR', 'Oman': 'OMN', 'Pakistan': 'PAK',
  'Palestine': 'PSE', 'Panama': 'PAN', 'Papua New Guinea': 'PNG',
  'Paraguay': 'PRY', 'Peru': 'PER', 'Philippines': 'PHL', 'Poland': 'POL',
  'Portugal': 'PRT', 'Qatar': 'QAT', 'Romania': 'ROU', 'Russia': 'RUS',
  'Rwanda': 'RWA', 'Saudi Arabia': 'SAU', 'Senegal': 'SEN', 'Serbia': 'SRB',
  'Sierra Leone': 'SLE', 'Singapore': 'SGP', 'Slovakia': 'SVK',
  'Slovenia': 'SVN', 'Solomon Islands': 'SLB', 'Somalia': 'SOM',
  'South Africa': 'ZAF', 'South Korea': 'KOR', 'South Sudan': 'SSD',
  'Spain': 'ESP', 'Sri Lanka': 'LKA', 'Sudan': 'SDN', 'Suriname': 'SUR',
  'Swaziland': 'SWZ', 'Sweden': 'SWE', 'Switzerland': 'CHE', 'Syria': 'SYR',
  'Taiwan': 'TWN', 'Tajikistan': 'TJK', 'Tanzania': 'TZA',
  'United Republic of Tanzania': 'TZA',
  'Thailand': 'THA', 'Togo': 'TGO', 'Trinidad and Tobago': 'TTO',
  'Tunisia': 'TUN', 'Turkey': 'TUR', 'Turkmenistan': 'TKM', 'Uganda': 'UGA',
  'Ukraine': 'UKR', 'United Arab Emirates': 'ARE', 'United Kingdom': 'GBR',
  'United States of America': 'USA', 'Uruguay': 'URY', 'Uzbekistan': 'UZB',
  'Vanuatu': 'VUT', 'Venezuela': 'VEN', 'Vietnam': 'VNM',
  'West Bank': 'PSE', 'Western Sahara': 'ESH', 'Yemen': 'YEM',
  'Zambia': 'ZMB', 'Zimbabwe': 'ZWE',
  'Cote d\'Ivoire': 'CIV', "Côte d'Ivoire": 'CIV',
  'Republic of the Congo': 'COG',
  'Northern Cyprus': 'CYP',
  'Somaliland': 'SOM',
  'Republic of Serbia': 'SRB',
  'The Bahamas': 'BHS',
  'Falkland Islands': 'FLK',
  'French Southern and Antarctic Lands': 'ATF',
  'New Caledonia': 'NCL',
  'Puerto Rico': 'PRI',
  'French Guiana': 'GUF',
  'Greenland': 'GRL',
  'Antarctica': 'ATA',
};

function isoFromName(name: string): string {
  return nameToIso3[name] || '';
}
